import type { ReactNode } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { MockAuth0Provider } from './mock-provider';
import { rememberReturnTo } from './return-to';
import { clearScreenLock } from './screen-lock';

// Picks the Auth0 provider implementation per docs/ui/operator-ui-tds.md §7
// and docs/ui/auth0-frontend-tds.md §4.2 / §14.2. NEX-144 lands the operator-
// specific overlay: pinned-org enforcement at the IdP, 30-min idle monitor,
// logout-with-WS-close ordering. This provider establishes the wiring those
// pieces hook into.

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const disabled = import.meta.env.VITE_AUTH0_DISABLE === 'true' && import.meta.env.DEV;

  if (disabled) {
    return <MockAuth0Provider>{children}</MockAuth0Provider>;
  }

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  const organization = import.meta.env.VITE_AUTH0_ORG;

  if (!domain || !clientId || !audience || !organization) {
    throw new Error(
      'Missing Auth0 env vars. Set VITE_AUTH0_DOMAIN / VITE_AUTH0_CLIENT_ID / VITE_AUTH0_AUDIENCE / VITE_AUTH0_ORG, ' +
        'or set VITE_AUTH0_DISABLE=true for offline dev.',
    );
  }

  return (
    <Auth0Provider
      // Capture where the operator was headed. Without this the SDK's default
      // callback runs, CallbackPage sends everyone to '/', and every deep link
      // into the console lands on the dashboard — see `return-to.ts`.
      //
      // A full-page unlock of the screen lock (screen-lock.tsx) comes back through here
      // too: the operator just typed their password at Auth0, so the lock is lifted.
      onRedirectCallback={(appState) => {
        if (appState?.unlockScreen === true) clearScreenLock();
        rememberReturnTo(appState?.returnTo);
      }}
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin + '/callback',
        audience,
        organization,
        scope: 'openid profile email offline_access',
      }}
      useRefreshTokens={true}
      useRefreshTokensFallback={false}
      cacheLocation="memory"
    >
      {children}
    </Auth0Provider>
  );
}
